import { ReactElement, Dispatch, useState } from 'react';
import { Pagination, styled } from '@mui/material';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IDeaEditMode, StyledButton } from '../common';

import { RequestAction, RegistrationNo } from '.';

import { map as lmap, compose as lcompose } from 'lodash/fp';

export const RegistrationNosEnroll = ({
  deaNos,
  dispatchRequest,

  tooltipValue,
  toggleTooltip,
  offTooltip,
  feedback,
}: {
  deaNos: IDeaEditMode[];
  dispatchRequest: Dispatch<RequestAction>;

  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
  feedback: string;
}) => {
  const editing = (key: string) =>
    dispatchRequest({ type: 'EdittingDeaNum', payload: key });

  const update = (a: IDeaEditMode) =>
    dispatchRequest({ type: 'UpdateDeaNum', payload: a });

  const remove = (key: string) =>
    dispatchRequest({ type: 'DeleteDeaNum', payload: key });

  const removeAll = lcompose(
    lmap<RequestAction, void>(dispatchRequest),
    lmap<IDeaEditMode, RequestAction>(({ key }) => ({
      type: 'DeleteDeaNum',
      payload: key,
    }))
  );

  const [page, setPage] = useState(1);
  const pageSize = 100;

  return (
    <DeaNumListSection aria-labelledby="dea-reg-num-list-label">
      {deaNos.length > 0 && (
        <DeaNumListHeader>
          <span id="dea-reg-num-list-label">DEA Registration Number List</span>

          <StyledButton onClick={() => removeAll(deaNos)}>
            <span>Remove all</span>
            <FontAwesomeIcon icon={faTrashAlt} size="1x" fixedWidth />
          </StyledButton>
        </DeaNumListHeader>
      )}
      {deaNos.length > pageSize && (
        <Paginatio
          count={Math.ceil(deaNos.length / pageSize)}
          page={page}
          onChange={(z, page) => setPage(page)}
          shape="rounded"
        />
      )}

      <Wrapper>
        {lmap<IDeaEditMode, ReactElement>(a => {
          switch (a.editMode) {
            case 'Read':
              return (
                <RegistrationNo
                  key={a.key}
                  item={a}
                  editing={editing}
                  remove={remove}
                  tooltipValue={tooltipValue}
                  toggleTooltip={toggleTooltip}
                  offTooltip={offTooltip}
                  feedback={feedback}
                />
              );

            case 'Update':
              return (
                <RegistrationNo
                  key={a.key}
                  item={a}
                  save={update}
                  tooltipValue={tooltipValue}
                  toggleTooltip={toggleTooltip}
                  offTooltip={offTooltip}
                  feedback={feedback}
                />
              );

            default:
              return <>NA</>;
          }
        })(deaNos.slice((page - 1) * pageSize, page * pageSize))}
      </Wrapper>

      {deaNos.length > pageSize && (
        <Paginatio
          count={Math.ceil(deaNos.length / pageSize)}
          page={page}
          onChange={(z, page) => setPage(page)}
          shape="rounded"
        />
      )}

      <div className="empty-state">
        <h4>Empty DEA Registration Number List</h4>
      </div>
    </DeaNumListSection>
  );
};

const DeaNumListSection = styled('article')(({ theme }) => ({
  '&>*+*': {
    marginTop: '1rem',
  },
}));

const DeaNumListHeader = styled('h4')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  '&>*+*': {
    marginLeft: '1rem !important',
  },
}));

const Wrapper = styled('ul')(({ theme }) => ({
  display: 'grid',

  gridTemplateColumns: '1fr',
  '@media (min-width: 60em)': {
    gridTemplateColumns: 'repeat(2, 35rem)',
  },
  '@media (min-width: 90em)': {
    gridTemplateColumns: 'repeat(3, 35rem)',
  },
  '@media (min-width: 120em)': {
    gridTemplateColumns: 'repeat(4, 35rem)',
  },

  gap: `1rem`,
}));

const Paginatio = styled(Pagination)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
}));
